import React, { useState, useRef, useEffect } from 'react';
import './DiligenceReport.css';

const SEVERITY_CONFIG = {
  CRITICAL: { label: 'CRITICAL', rowClass: 'row-critical', textClass: 'text-critical', badgeClass: 'badge-critical' },
  HIGH:     { label: 'HIGH',     rowClass: 'row-high',     textClass: 'text-high',     badgeClass: 'badge-high' },
  MEDIUM:   { label: 'MEDIUM',   rowClass: 'row-medium',   textClass: 'text-medium',   badgeClass: 'badge-medium' },
};

function FlagBadge({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.MEDIUM;
  return <span className={`flag-badge ${cfg.badgeClass}`}>{cfg.label}</span>;
}

function FlagTable({ table }) {
  if (!table || !table.columns?.length) return null;
  const highlightSet = new Set(table.highlight_rows || []);
  return (
    <div className="flag-table-wrapper">
      {table.caption && <p className="flag-table-caption">{table.caption}</p>}
      <table className="flag-detail-table">
        <thead>
          <tr>{table.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {(table.rows || []).map((row, ri) => (
            <tr key={ri} className={highlightSet.has(ri) ? 'highlight-row' : ''}>
              {row.map((cell, ci) => <td key={ci}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlagDetail({ flag }) {
  const cfg = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.MEDIUM;
  return (
    <div className="flag-detail-block">
      <div className={`flag-detail-title ${cfg.textClass}`}>
        {flag.id} — {flag.area} ({flag.severity})
      </div>
      <div className="flag-detail-body">
        {flag.detail?.split('\n').filter(Boolean).map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>
      {(flag.tables || []).map((t, i) => <FlagTable key={i} table={t} />)}
      {flag.action_required && (
        <p className="flag-action-required">
          <strong>Action required:</strong> {flag.action_required}
        </p>
      )}
    </div>
  );
}

export default function DiligenceReport({ report, dealName, analyzedAt, onAddToNotes, dealTypeId }) {
  const [expandedFlags, setExpandedFlags] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [selectionTooltip, setSelectionTooltip] = useState(null); // { text, x, y }
  const reportRef = useRef(null);

  // Listen at document level so it fires regardless of which child element is under cursor
  useEffect(() => {
    console.log('[DDReport] useEffect onAddToNotes:', !!onAddToNotes);
    if (!onAddToNotes) return;
    console.log('[DDReport] registering document mouseup listener');

    const handleMouseUp = () => {
      console.log('[DDReport] doc mouseup fired');
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (!text || text.length < 10) {
        setSelectionTooltip(null);
        return;
      }
      if (!reportRef.current?.contains(selection.anchorNode)) {
        setSelectionTooltip(null);
        return;
      }
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = reportRef.current.getBoundingClientRect();
      setSelectionTooltip({
        text,
        x: rect.left + rect.width / 2 - containerRect.left,
        y: rect.top - containerRect.top - 8,
      });
    };

    const handleMouseDown = (e) => {
      // Don't dismiss if clicking the tooltip itself
      if (e.target.closest('.dd-selection-tooltip')) return;
      setSelectionTooltip(null);
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onAddToNotes]);

  const handleAddToNotes = () => {
    if (selectionTooltip) {
      onAddToNotes(selectionTooltip.text);
      setSelectionTooltip(null);
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleDownload = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `${(dealName || 'Deal').replace(/[^a-z0-9]/gi, '_')}_DD_Report_${new Date().toISOString().slice(0,10)}.pdf`;
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(reportRef.current)
        .save();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!report) {
    return (
      <div className="dd-report-empty">
        <p>No DD report generated yet. Run the AI analysis to generate a formal due diligence assessment report.</p>
      </div>
    );
  }

  const flags = report.flags || [];
  const criticalCount = flags.filter(f => f.severity === 'CRITICAL').length;
  const highCount = flags.filter(f => f.severity === 'HIGH').length;
  const mediumCount = flags.filter(f => f.severity === 'MEDIUM').length;

  // Group flags by section
  const sections = {};
  flags.forEach(flag => {
    const sec = flag.section || 'Other';
    if (!sections[sec]) sections[sec] = {};
    const sub = flag.subsection || 'General';
    if (!sections[sec][sub]) sections[sec][sub] = [];
    sections[sec][sub].push(flag);
  });

  const sectionOrder = [
    'Financial Due Diligence',
    'Operational Due Diligence',
    'Human Capital Due Diligence',
    'Legal & Contractual Due Diligence',
    'Governance & Ownership Due Diligence',
  ];

  const orderedSections = [
    ...sectionOrder.filter(s => sections[s]),
    ...Object.keys(sections).filter(s => !sectionOrder.includes(s)),
  ];

  const toggleFlag = (id) => setExpandedFlags(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="dd-report-wrapper">
      {/* Download button — outside the printable area */}
      <div className="dd-download-bar">
        <button
          className={`dd-download-btn${downloading ? ' dd-download-btn--loading' : ''}`}
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <span className="dd-spinner" />
              Generating PDF…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download PDF
            </>
          )}
        </button>
      </div>

    <div className="dd-report" ref={reportRef}>
      {selectionTooltip && (
        <div
          className="dd-selection-tooltip"
          style={{ left: selectionTooltip.x, top: selectionTooltip.y }}
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
        >
          <button className="dd-selection-tooltip-btn" onClick={handleAddToNotes}>
            + Add to Finding Notes
          </button>
        </div>
      )}

      {/* Header */}
      <div className="dd-report-header">
        <h1 className="dd-company-name">{dealName?.toUpperCase() || 'COMPANY NAME'}</h1>
        <h2 className="dd-subtitle">Due Diligence Assessment — Acquisition Review</h2>

        <table className="dd-meta-table">
          <tbody>
            <tr><td className="meta-label">Deal Stage</td><td>{report.deal_stage || '—'}</td></tr>
            <tr><td className="meta-label">Deal Type</td><td>{report.deal_type || '—'}</td></tr>
            <tr><td className="meta-label">Reviewer</td><td>{report.reviewer || 'Partner'}</td></tr>
            <tr><td className="meta-label">Date</td><td>{
              analyzedAt
                ? new Date(analyzedAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            }</td></tr>
            <tr>
              <td className="meta-label">Status</td>
              <td className="meta-draft">{report.status || 'DRAFT — NOT FOR DISTRIBUTION'}</td>
            </tr>
            <tr>
              <td className="meta-label">Flag Count</td>
              <td>
                <span className="flag-count-critical">{criticalCount} CRITICAL</span>
                <span className="flag-count-sep"> | </span>
                <span className="flag-count-high">{highCount} HIGH</span>
                <span className="flag-count-sep"> | </span>
                <span className="flag-count-medium">{mediumCount} MEDIUM</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Sale-Leaseback Specific Layout */}
      {(dealTypeId || report.deal_type_id) === 'sale_leaseback_industrial' && (
        <>
          {/* Two-column: Property Track vs Tenant Credit Track */}
          <div className="dd-section">
            <h2 className="dd-section-title"><span className="section-num">1.</span> Dual-Track Assessment</h2>
            <div className="dd-divider" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Property Track */}
              <div style={{ border: '1px solid #DBEAFE', borderRadius: 8, padding: 16, background: '#F8FAFF' }}>
                <h3 style={{ color: '#1E40AF', margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Property Track</h3>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Property Risk Score</span>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1E40AF' }}>
                    {report.property_risk_score ?? '—'}
                  </div>
                </div>
                {(report.property_flags || []).length > 0 && (
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>Property Flags</span>
                    <ul style={{ margin: '8px 0 0', paddingLeft: 16, fontSize: 13, color: '#374151' }}>
                      {(report.property_flags || []).map((flag, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          <FlagBadge severity={flag.severity} /> {flag.summary || flag.area}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Tenant Credit Track */}
              <div style={{ border: '1px solid #FFEDD5', borderRadius: 8, padding: 16, background: '#FFFBF5' }}>
                <h3 style={{ color: '#C2410C', margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Tenant Credit Track</h3>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Tenant Credit Score</span>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#C2410C' }}>
                    {report.tenant_credit_score ?? '—'}
                  </div>
                </div>
                {(report.credit_flags || []).length > 0 && (
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>Credit Flags</span>
                    <ul style={{ margin: '8px 0 0', paddingLeft: 16, fontSize: 13, color: '#374151' }}>
                      {(report.credit_flags || []).map((flag, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          <FlagBadge severity={flag.severity} /> {flag.summary || flag.area}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lease Coverage Ratio Banner */}
          <div className="dd-section" style={{ textAlign: 'center' }}>
            <div style={{
              background: (report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.5) ? '#ECFDF5' :
                          (report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.2) ? '#FFFBEB' : '#FEF2F2',
              border: `2px solid ${(report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.5) ? '#10B981' :
                      (report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.2) ? '#F59E0B' : '#EF4444'}`,
              borderRadius: 12,
              padding: '24px 32px',
            }}>
              <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>Lease Coverage Ratio</span>
              <div style={{
                fontSize: 48,
                fontWeight: 800,
                color: (report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.5) ? '#059669' :
                       (report.lease_coverage_ratio && report.lease_coverage_ratio >= 1.2) ? '#D97706' : '#DC2626',
                lineHeight: 1.2,
                marginTop: 4,
              }}>
                {report.lease_coverage_ratio ? `${report.lease_coverage_ratio.toFixed(2)}x` : '—'}
              </div>
              <span style={{ fontSize: 12, color: '#6B7280' }}>
                {report.lease_coverage_ratio >= 1.5 ? 'Strong coverage' :
                 report.lease_coverage_ratio >= 1.2 ? 'Adequate coverage' : 'Below threshold'}
              </span>
            </div>
          </div>

          {/* Combined Analysis / Cross-Track Flags / Deal Recommendation */}
          <div className="dd-section">
            <h2 className="dd-section-title"><span className="section-num">2.</span> Combined Analysis</h2>
            <div className="dd-divider" />
            {(report.combined_analysis || []).map((para, i) => (
              <p key={i} className="dd-para">{para}</p>
            ))}

            {(report.cross_track_flags || []).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Cross-Track Flags</h3>
                {(report.cross_track_flags || []).map((flag, i) => (
                  <div key={i} style={{ marginBottom: 8, padding: '8px 12px', background: '#FEF3C7', borderRadius: 6, fontSize: 13 }}>
                    <FlagBadge severity={flag.severity} /> <strong>{flag.area}</strong> — {flag.summary}
                  </div>
                ))}
              </div>
            )}

            {report.deal_recommendation && (
              <div style={{ marginTop: 20, padding: 16, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#166534', margin: '0 0 8px' }}>Deal Recommendation</h3>
                <p className="dd-para" style={{ margin: 0 }}>{report.deal_recommendation}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Section 1: Executive Summary (standard layout for non-SLB deals) */}
      {(dealTypeId || report.deal_type_id) !== 'sale_leaseback_industrial' && (
      <div className="dd-section">
        <h2 className="dd-section-title"><span className="section-num">1.</span> Executive Summary</h2>
        <div className="dd-divider" />
        {(report.executive_summary_paragraphs || []).map((para, i) => (
          <p key={i} className="dd-para">{para}</p>
        ))}

        {report.financial_snapshot?.length > 0 && (
          <div className="dd-snapshot">
            <p className="dd-snapshot-title"><strong>Financial Snapshot (as Reported)</strong></p>
            <table className="dd-data-table">
              <thead><tr><th>Metric</th><th>Value</th></tr></thead>
              <tbody>
                {report.financial_snapshot.map((row, i) => (
                  <tr key={i}><td><strong>{row.metric}</strong></td><td>{row.value}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Section 2: Consolidated Flag Register */}
      <div className="dd-section">
        <h2 className="dd-section-title"><span className="section-num">2.</span> Consolidated Flag Register</h2>
        <div className="dd-divider" />
        <p className="dd-para">
          All flags identified across the due diligence review are catalogued below. Severity definitions:
          <strong> CRITICAL</strong> = deal-stopper requiring resolution before LOI;
          <strong> HIGH</strong> = material item requiring management response and diligence validation;
          <strong> MEDIUM</strong> = requires monitoring, modeling, or contractual protection.
        </p>
        <table className="dd-flag-register">
          <thead>
            <tr><th>ID</th><th>Severity</th><th>Area</th><th>Summary</th></tr>
          </thead>
          <tbody>
            {flags.map(flag => {
              const cfg = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.MEDIUM;
              return (
                <tr
                  key={flag.id}
                  className={`${cfg.rowClass} flag-register-row`}
                  onClick={() => toggleFlag(flag.id)}
                  title="Click to expand detail"
                >
                  <td className={`flag-id ${cfg.textClass}`}>{flag.id}</td>
                  <td className={`flag-severity ${cfg.textClass}`}><strong>{flag.severity}</strong></td>
                  <td>{flag.area}</td>
                  <td>{flag.summary}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="dd-table-note">Click any row to expand the detailed analysis.</p>

        {/* Expanded flag details inline */}
        {flags.filter(f => expandedFlags[f.id]).map(flag => (
          <FlagDetail key={flag.id} flag={flag} />
        ))}
      </div>

      {/* Sections 3–7: Detailed DD sections */}
      {orderedSections.map((sectionName, sIdx) => {
        const sectionNum = sIdx + 3;
        const subsections = sections[sectionName];
        return (
          <div key={sectionName} className="dd-section">
            <h2 className="dd-section-title">
              <span className="section-num">{sectionNum}.</span> {sectionName}
            </h2>
            <div className="dd-divider" />

            {Object.entries(subsections).map(([subName, subFlags]) => (
              <div key={subName} className="dd-subsection">
                <h3 className="dd-subsection-title">
                  <span className="section-num">{sectionNum}.{Object.keys(subsections).indexOf(subName) + 1}</span>{' '}
                  {subName}
                </h3>
                {subFlags.map(flag => (
                  <div key={flag.id}>
                    <FlagDetail flag={flag} />
                    <div className="dd-flag-divider" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}

      {/* Section N: Required Next Steps */}
      {report.next_steps?.length > 0 && (
        <div className="dd-section">
          <h2 className="dd-section-title">
            <span className="section-num">{orderedSections.length + 3}.</span> Required Next Steps Before LOI
          </h2>
          <div className="dd-divider" />
          <p className="dd-para">
            The following items must be resolved before a Letter of Intent can be submitted with defensible financial assumptions. Items are listed in priority order.
          </p>
          {report.next_steps.map((group, i) => (
            <div key={i} className="next-steps-group">
              <p className={`next-steps-heading severity-${group.severity?.toLowerCase()}`}>
                <strong>{group.severity} — {group.category}</strong>
              </p>
              <ul className="next-steps-list">
                {(group.items || []).map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Section N+1: Documents Reviewed */}
      {report.documents_reviewed?.length > 0 && (
        <div className="dd-section">
          <h2 className="dd-section-title">
            <span className="section-num">{orderedSections.length + 4}.</span> Documents Reviewed
          </h2>
          <div className="dd-divider" />
          <p className="dd-para">The following documents were reviewed and analyzed in the preparation of this assessment.</p>
          <table className="dd-data-table">
            <thead><tr><th>Category</th><th>Document</th></tr></thead>
            <tbody>
              {report.documents_reviewed.map((doc, i) => (
                <tr key={i}><td>{doc.category}</td><td>{doc.document}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="dd-footer">
        <span className="dd-footer-confidential">CONFIDENTIAL — DRAFT FOR INTERNAL USE ONLY</span>
        {' | '}
        This document is prepared for the exclusive use of the acquiring party and its advisors. Additional diligence, management interviews, and third-party expert review are required before any investment decision can be made.
      </div>
    </div>
    </div>
  );
}
